import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CachePolicies } from "use-http";

import { ConnectedHubIcon, DisconnectedHubIcon } from "assets/icons";
import { HubSubNavBar } from "components/hubs/HubSubNavBar";
import { JANZU_URL } from "config/config";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom/useFetchCustom";
import { DeviceApp, SystemInfo } from "models/hub";
import { Typography } from "new-components/typography/Typography";
import { getHub } from "store/services/Hubs/hubsThunks";
import { handleResetHubs, setCurrentHub } from "store/slices/Hubs/hubsSlice";
import { DATETIME_FORMAT } from "utils/date";

export function HubStatusPageRoute() {
  const { id } = useParams<{ id?: string }>();
  return id ? <HubStatusPage hubId={id} /> : <div>Please provide an id in the URL!</div>;
}

interface HubStatusPageProps {
  hubId: string;
}

// Component for displaying "Information not available" text
function InfoNotAvailable(): JSX.Element {
  return (
    <Typography color="text.secondary" fontStyle="italic">
      Information not available
    </Typography>
  );
}

export function HubStatusPage({ hubId }: HubStatusPageProps) {
  const { currentHub, hubLoading, error } = useAppSelector((state) => state.hubs);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [systemInfoError, setSystemInfoError] = useState<string | null>(null);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const handleBackClick = () => {
    navigate("/reef/hubs");
  };

  const {
    loading: systemInfoLoading,
    error: systemInfoFetchError,
    data: systemInfoData,
    get: fetchSystemInfo,
  } = useFetchCustom<SystemInfo>(currentHub?.id ? `${JANZU_URL}reef-api/hubs/${currentHub.id}/system-info` : "", {
    cachePolicy: CachePolicies.NO_CACHE,
    shouldShowErrorSnackbar: (response) => response.status === 500,
    retries: 0,
  });

  const pollSystemInfo = useCallback(async () => {
    if (currentHub?.balena_device_id) {
      try {
        await fetchSystemInfo();
      } finally {
        // Schedule next poll after the current one completes (success or error)
        timeoutId.current = setTimeout(pollSystemInfo, 10000);
      }
    }
  }, [currentHub?.balena_device_id, fetchSystemInfo]);

  useEffect(() => {
    if (currentHub?.balena_device_id) {
      // Initial fetch
      pollSystemInfo();
    }

    // Clean up timeout on component unmount or when dependencies change
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [currentHub?.balena_device_id, pollSystemInfo]);

  useEffect(() => {
    const fetchHubData = async () => {
      const hub = await dispatch(getHub({ hubId }));
      dispatch(setCurrentHub(hub.payload));
    };

    fetchHubData();

    return () => {
      dispatch(handleResetHubs());
    };
  }, [hubId, dispatch]);

  useEffect(() => {
    // If systemInfoData is not null and does not contain an error, set it to state
    if (systemInfoData && !systemInfoError) {
      setSystemInfo(systemInfoData);
    } else {
      setSystemInfo(null);
    }
  }, [systemInfoData, systemInfoError]);

  useEffect(() => {
    if (systemInfoFetchError || systemInfoData?.error) {
      setSystemInfoError("Failed to fetch system information");
    }

  }, [systemInfoFetchError, systemInfoData]);


  const getStatusIcon = (isOnline: boolean) => (isOnline ? <ConnectedHubIcon /> : <DisconnectedHubIcon />);

  const getStatusText = (isOnline: boolean) => (isOnline ? "Online" : "Offline");

  return (
    <>
      <HubSubNavBar currentHub={currentHub} currentPage="Vision Core status" />
      <Box sx={{ height: "calc(100vh - 150px)", overflow: "auto" }}>
        {hubLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress size={40} />
          </Box>
        )}

        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="subtitle" color="error">
              Error loading Vision Core data: {error}
            </Typography>
          </Box>
        )}

        {!hubLoading && !error && currentHub && (
          <Container maxWidth="md" sx={{ padding: "20px 30px!important" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <IconButton
                onClick={handleBackClick}
                sx={{ mr: 1 }}
                aria-label="back to Vision Cores list"
                data-testid="back-to-hubs-button"
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography sx={{ cursor: "pointer" }} onClick={handleBackClick}>
                Back
              </Typography>
            </Box>

            {/* Vision Core Identification Section */}
            <Paper sx={{ mb: 3, p: 2 }}>
              <Typography variant="subtitle" gutterBottom>
                Vision Core Identification
              </Typography>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: "bold", width: "30%" }}>
                        Name
                      </TableCell>
                      <TableCell>{currentHub.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                        Vision Core ID
                      </TableCell>
                      <TableCell>{currentHub.id}</TableCell>
                    </TableRow>
                    {currentHub.balena_device_id && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Balena Device ID
                        </TableCell>
                        <TableCell>{currentHub.balena_device_id}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                        Created At
                      </TableCell>
                      <TableCell>{dayjs(currentHub.created_at).format(DATETIME_FORMAT)}</TableCell>
                    </TableRow>
                    {currentHub.repository_url && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Repository URL
                        </TableCell>
                        <TableCell>
                          <a href={currentHub.repository_url} target="_blank" rel="noopener noreferrer">
                            {currentHub.repository_url}
                          </a>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Connectivity Status Section */}
            {currentHub.status_detail && (
              <Paper sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle" gutterBottom>
                  Connectivity Status
                </Typography>
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold", width: "30%" }}>
                          Vision Core Status
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(!!currentHub.status_detail.is_online)}
                            {getStatusText(!!currentHub.status_detail.is_online)}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Services Online
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(!!currentHub.status_detail.services_online)}
                            {getStatusText(!!currentHub.status_detail.services_online)}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Main App Online
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(!!currentHub.status_detail.main_app_online)}
                            {getStatusText(!!currentHub.status_detail.main_app_online)}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Vision Core Online
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(!!currentHub.status_detail.hub_online)}
                            {getStatusText(!!currentHub.status_detail.hub_online)}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          All Services Online
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(!!currentHub.status_detail.all_online)}
                            {getStatusText(!!currentHub.status_detail.all_online)}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Endpoint Reachability
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(!!currentHub.status_detail.endpoint_reachable)}
                            {getStatusText(!!currentHub.status_detail.endpoint_reachable)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* API Configuration Section */}
            <Paper sx={{ mb: 3, p: 2 }}>
              <Typography variant="subtitle" gutterBottom>
                API Configuration
              </Typography>
              {systemInfoLoading && !systemInfo && !systemInfoError && (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={30} />
                </Box>
              )}
              {systemInfoError && !systemInfo && (
                <Typography color="error" align="center" p={2}>
                  {systemInfoError}
                </Typography>
              )}
              {systemInfo && (
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold", width: "30%" }}>
                          Groundlight Endpoint
                        </TableCell>
                        <TableCell>{systemInfo["Groundlight Endpoint"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          API Token Configuration
                        </TableCell>
                        <TableCell>{systemInfo["API Token Configured"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Groundlight Username
                        </TableCell>
                        <TableCell>{systemInfo["Groundlight Username"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* System Information Section */}
            <Paper sx={{ mb: 3, p: 2 }}>
              <Typography variant="subtitle" gutterBottom>
                System Information
              </Typography>
              {systemInfoLoading && !systemInfo && !systemInfoError && (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={30} />
                </Box>
              )}
              {systemInfoError && !systemInfo && (
                <Typography color="error" align="center" p={2}>
                  {systemInfoError}
                </Typography>
              )}
              {systemInfo && (
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold", width: "30%" }}>
                          Hostname / Container ID
                        </TableCell>
                        <TableCell>{systemInfo["Hostname / Container ID"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Local IP Address
                        </TableCell>
                        <TableCell>
                          {systemInfo["Local IP Address"] !== "Unavailable" ? (
                            systemInfo["Local IP Address"]
                          ) : (
                            <InfoNotAvailable />
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Uptime
                        </TableCell>
                        <TableCell>{systemInfo.Uptime || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          CPU Load Average (1 min)
                        </TableCell>
                        <TableCell>{systemInfo["CPU Load Average (1 min)"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          CPU Load Average (5 min)
                        </TableCell>
                        <TableCell>{systemInfo["CPU Load Average (5 min)"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          CPU Load Average (15 min)
                        </TableCell>
                        <TableCell>{systemInfo["CPU Load Average (15 min)"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          CPU Cores
                        </TableCell>
                        <TableCell>
                          {systemInfo["CPU Cores"] !== undefined ? systemInfo["CPU Cores"] : <InfoNotAvailable />}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          CPU Architecture
                        </TableCell>
                        <TableCell>{systemInfo["CPU Architecture"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Total RAM
                        </TableCell>
                        <TableCell>{systemInfo["Total RAM"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                          Free Disk Space
                        </TableCell>
                        <TableCell>{systemInfo["Free Disk Space"] || <InfoNotAvailable />}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Installed Applications Section */}
            {currentHub.device_apps && currentHub.device_apps.length > 0 && (
              <Paper sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle" gutterBottom>
                  Installed Applications
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>App Name</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentHub.device_apps.map((app: DeviceApp) => {
                        const appState = currentHub.status_detail?.app_states?.find(
                          (state) => state.app_name === app.app_name
                        );
                        const isOnline = appState ? appState.status : false;

                        return (
                          <TableRow key={app.app_num}>
                            <TableCell>{app.app_name}</TableCell>
                            <TableCell>{app.app_description}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {getStatusIcon(isOnline)}
                                {getStatusText(isOnline)}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Additional Information */}
            {currentHub.isRestarting && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle" gutterBottom>
                  Restart Status
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography>
                    Vision Core is currently restarting. This may take more than 5 minutes depending on
                    configuration.
                  </Typography>
                </Box>
              </Paper>
            )}
          </Container>
        )}
      </Box>
    </>
  );
}
